import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Calendar, Plus, Check, X, Clock, Filter,
  Send, AlertCircle, CheckCircle, XCircle, Trash2, Coffee
} from 'lucide-react';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LEAVE_TYPES = [
  { value: 'casual', label: 'Casual Leave', color: '#3B82F6' },
  { value: 'sick', label: 'Sick Leave', color: '#EF4444' },
  { value: 'annual', label: 'Annual Leave', color: '#10B981' },
  { value: 'personal', label: 'Personal Leave', color: '#F59E0B' },
  { value: 'unpaid', label: 'Unpaid Leave', color: '#6B7280' },
  { value: 'other', label: 'Other', color: '#8B5CF6' }
];

const LeaveManagement = ({ user }) => {
  const [myLeaves, setMyLeaves] = useState([]);
  const [pendingApprovals, setPendingApprovals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('my-leaves');
  const [showApplyModal, setShowApplyModal] = useState(false);
  const [statusFilter, setStatusFilter] = useState('');
  const [formData, setFormData] = useState({
    leave_type: 'casual',
    start_date: '',
    end_date: '',
    reason: ''
  });

  const isManager = ['superadmin', 'ceo', 'manager', 'team_lead'].includes(user?.role);

  const fetchMyLeaves = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const params = new URLSearchParams();
      if (statusFilter) params.append('status', statusFilter);
      
      const response = await axios.get(`${API_URL}/leave-requests?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      // Filter to only show current user's leaves
      const myLeavesOnly = response.data.filter(l => l.user_id === user.id);
      setMyLeaves(myLeavesOnly);
    } catch (error) {
      console.error('Failed to fetch leave requests:', error);
    }
  }, [statusFilter, user.id]);

  const fetchPendingApprovals = useCallback(async () => {
    if (!isManager) return;
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/leave-requests/pending`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPendingApprovals(response.data);
    } catch (error) {
      console.error('Failed to fetch pending approvals:', error);
    }
  }, [isManager]);

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([fetchMyLeaves(), fetchPendingApprovals()]);
      setLoading(false);
    };
    loadData();
  }, [fetchMyLeaves, fetchPendingApprovals]);

  const handleApplyLeave = async (e) => {
    e.preventDefault();
    
    if (!formData.start_date || !formData.end_date || !formData.reason.trim()) {
      toast.error('Please fill all required fields');
      return;
    }

    if (new Date(formData.end_date) < new Date(formData.start_date)) {
      toast.error('End date cannot be before start date');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API_URL}/leave-requests`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Leave request submitted successfully');
      setShowApplyModal(false);
      setFormData({ leave_type: 'casual', start_date: '', end_date: '', reason: '' });
      fetchMyLeaves();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit leave request');
    }
  };

  const handleApproveReject = async (requestId, status, rejectionReason = '') => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API_URL}/leave-requests/${requestId}`, {
        status,
        rejection_reason: rejectionReason
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Leave request ${status}`);
      fetchPendingApprovals();
      fetchMyLeaves();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update leave request');
    }
  };

  const handleCancelLeave = async (requestId) => {
    if (!window.confirm('Are you sure you want to cancel this leave request?')) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API_URL}/leave-requests/${requestId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Leave request cancelled');
      fetchMyLeaves();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to cancel leave request');
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      pending: { bg: '#FEF3C7', color: '#D97706', icon: Clock },
      approved: { bg: '#D1FAE5', color: '#059669', icon: CheckCircle },
      rejected: { bg: '#FEE2E2', color: '#DC2626', icon: XCircle },
      cancelled: { bg: '#F3F4F6', color: '#6B7280', icon: X }
    };
    const style = styles[status] || styles.pending;
    const Icon = style.icon;
    return (
      <span style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: '6px',
        padding: '4px 12px',
        borderRadius: '20px',
        fontSize: '12px',
        fontWeight: '600',
        background: style.bg,
        color: style.color
      }}>
        <Icon size={14} />
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </span>
    );
  };

  const getLeaveTypeBadge = (type) => {
    const leaveType = LEAVE_TYPES.find(lt => lt.value === type) || LEAVE_TYPES[5];
    return (
      <span style={{
        padding: '4px 10px',
        borderRadius: '6px',
        fontSize: '11px',
        fontWeight: '600',
        background: `${leaveType.color}20`,
        color: leaveType.color
      }}>
        {leaveType.label}
      </span>
    );
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr + 'T00:00:00').toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const calculateDays = (start, end) => {
    const startDate = new Date(start);
    const endDate = new Date(end);
    const diffTime = Math.abs(endDate - startDate);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;
    return diffDays;
  };

  if (loading) {
    return (
      <div className="content-area" style={{ padding: '40px', textAlign: 'center' }}>
        <div className="loading-spinner"></div>
        <p style={{ marginTop: '16px', color: 'var(--text-secondary)' }}>Loading leave management...</p>
      </div>
    );
  }

  return (
    <div className="content-area" data-testid="leave-management-page">
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px', flexWrap: 'wrap', gap: '16px' }}>
        <div>
          <h1 style={{ margin: 0, fontSize: '28px', fontWeight: '700', color: 'var(--text-primary)', display: 'flex', alignItems: 'center', gap: '12px' }}>
            <Coffee size={32} color="#F59E0B" />
            Leave Management
          </h1>
          <p style={{ margin: '8px 0 0 0', color: 'var(--text-secondary)' }}>Apply for leave and manage requests</p>
        </div>
        <button
          onClick={() => setShowApplyModal(true)}
          className="gradient-btn"
          style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '12px 24px' }}
          data-testid="apply-leave-btn"
        >
          <Plus size={20} />
          Apply for Leave
        </button>
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: '8px', marginBottom: '24px', borderBottom: '1px solid var(--border-color)', paddingBottom: '8px' }}>
        <button
          onClick={() => setActiveTab('my-leaves')}
          style={{
            padding: '10px 20px',
            background: activeTab === 'my-leaves' ? '#7077FE' : 'transparent',
            color: activeTab === 'my-leaves' ? 'white' : 'var(--text-secondary)',
            border: 'none',
            borderRadius: '8px',
            cursor: 'pointer',
            fontWeight: '500',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}
          data-testid="tab-my-leaves"
        >
          <Calendar size={18} />
          My Leaves
          {myLeaves.filter(l => l.status === 'pending').length > 0 && (
            <span style={{
              background: '#F59E0B',
              color: 'white',
              padding: '2px 8px',
              borderRadius: '10px',
              fontSize: '11px',
              fontWeight: '700'
            }}>
              {myLeaves.filter(l => l.status === 'pending').length}
            </span>
          )}
        </button>
        {isManager && (
          <button
            onClick={() => setActiveTab('approvals')}
            style={{
              padding: '10px 20px',
              background: activeTab === 'approvals' ? '#7077FE' : 'transparent',
              color: activeTab === 'approvals' ? 'white' : 'var(--text-secondary)',
              border: 'none',
              borderRadius: '8px',
              cursor: 'pointer',
              fontWeight: '500',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}
            data-testid="tab-approvals"
          >
            <CheckCircle size={18} />
            Approvals
            {pendingApprovals.length > 0 && (
              <span style={{
                background: '#EF4444',
                color: 'white',
                padding: '2px 8px',
                borderRadius: '10px',
                fontSize: '11px',
                fontWeight: '700'
              }}>
                {pendingApprovals.length}
              </span>
            )}
          </button>
        )}
      </div>

      {/* My Leaves Tab */}
      {activeTab === 'my-leaves' && (
        <div className="card" style={{ padding: '24px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', flexWrap: 'wrap', gap: '16px' }}>
            <h3 style={{ margin: 0, fontSize: '16px', fontWeight: '600', color: 'var(--text-primary)' }}>
              My Leave Requests
            </h3>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <Filter size={16} color="var(--text-tertiary)" />
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                style={{
                  padding: '8px 16px',
                  border: '1px solid var(--border-color)',
                  borderRadius: '8px',
                  background: 'var(--bg-primary)',
                  color: 'var(--text-primary)',
                  fontSize: '13px'
                }}
              >
                <option value="">All Status</option>
                <option value="pending">Pending</option>
                <option value="approved">Approved</option>
                <option value="rejected">Rejected</option>
                <option value="cancelled">Cancelled</option>
              </select>
            </div>
          </div>

          {myLeaves.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '40px', color: 'var(--text-tertiary)' }}>
              <Calendar size={48} style={{ marginBottom: '16px', opacity: 0.5 }} />
              <p>No leave requests found</p>
              <button
                onClick={() => setShowApplyModal(true)}
                className="gradient-btn"
                style={{ marginTop: '16px', padding: '10px 20px' }}
              >
                Apply for Leave
              </button>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {myLeaves.map(leave => (
                <div
                  key={leave.id}
                  style={{
                    padding: '16px 20px',
                    background: 'var(--bg-secondary)',
                    borderRadius: '12px',
                    border: '1px solid var(--border-color)'
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '12px' }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
                        {getLeaveTypeBadge(leave.leave_type)}
                        {getStatusBadge(leave.status)}
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '8px' }}>
                        <span style={{ color: 'var(--text-primary)', fontWeight: '500' }}>
                          {formatDate(leave.start_date)} - {formatDate(leave.end_date)}
                        </span>
                        <span style={{ color: 'var(--text-tertiary)', fontSize: '13px' }}>
                          ({calculateDays(leave.start_date, leave.end_date)} day{calculateDays(leave.start_date, leave.end_date) > 1 ? 's' : ''})
                        </span>
                      </div>
                      <p style={{ margin: 0, color: 'var(--text-secondary)', fontSize: '14px' }}>
                        <strong>Reason:</strong> {leave.reason}
                      </p>
                      {leave.manager_name && (
                        <p style={{ margin: '8px 0 0 0', color: 'var(--text-tertiary)', fontSize: '12px' }}>
                          Approver: {leave.manager_name}
                        </p>
                      )}
                      {leave.rejection_reason && (
                        <p style={{ margin: '8px 0 0 0', color: '#EF4444', fontSize: '13px' }}>
                          <strong>Rejection reason:</strong> {leave.rejection_reason}
                        </p>
                      )}
                    </div>
                    {leave.status === 'pending' && (
                      <button
                        onClick={() => handleCancelLeave(leave.id)}
                        style={{
                          padding: '8px 16px',
                          background: 'rgba(239, 68, 68, 0.1)',
                          border: 'none',
                          borderRadius: '8px',
                          color: '#EF4444',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '6px',
                          fontWeight: '500',
                          fontSize: '13px'
                        }}
                        data-testid={`cancel-leave-${leave.id}`}
                      >
                        <Trash2 size={14} />
                        Cancel
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Approvals Tab */}
      {activeTab === 'approvals' && isManager && (
        <div className="card" style={{ padding: '24px' }}>
          <h3 style={{ margin: '0 0 20px 0', fontSize: '16px', fontWeight: '600', color: 'var(--text-primary)' }}>
            Pending Approvals
          </h3>

          {pendingApprovals.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '40px', color: 'var(--text-tertiary)' }}>
              <CheckCircle size={48} style={{ marginBottom: '16px', opacity: 0.5 }} />
              <p>No pending approvals</p>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              {pendingApprovals.map(leave => (
                <div
                  key={leave.id}
                  style={{
                    padding: '20px',
                    background: 'var(--bg-secondary)',
                    borderRadius: '12px',
                    border: '1px solid var(--border-color)',
                    borderLeft: '4px solid #F59E0B'
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '16px' }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
                        <div style={{
                          width: '40px',
                          height: '40px',
                          borderRadius: '50%',
                          background: '#7077FE',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: 'white',
                          fontWeight: '600',
                          fontSize: '16px'
                        }}>
                          {leave.user_name?.charAt(0) || '?'}
                        </div>
                        <div>
                          <div style={{ fontWeight: '600', color: 'var(--text-primary)' }}>{leave.user_name}</div>
                          <div style={{ fontSize: '12px', color: 'var(--text-tertiary)' }}>{leave.user_email}</div>
                        </div>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
                        {getLeaveTypeBadge(leave.leave_type)}
                        <span style={{ color: 'var(--text-secondary)', fontSize: '13px' }}>
                          {formatDate(leave.start_date)} - {formatDate(leave.end_date)}
                        </span>
                        <span style={{ color: 'var(--text-tertiary)', fontSize: '12px' }}>
                          ({calculateDays(leave.start_date, leave.end_date)} day{calculateDays(leave.start_date, leave.end_date) > 1 ? 's' : ''})
                        </span>
                      </div>
                      <p style={{ margin: 0, color: 'var(--text-secondary)', fontSize: '14px' }}>
                        {leave.reason}
                      </p>
                      <p style={{ margin: '8px 0 0 0', color: 'var(--text-tertiary)', fontSize: '12px' }}>
                        Applied: {new Date(leave.created_at).toLocaleDateString()}
                      </p>
                    </div>
                    <div style={{ display: 'flex', gap: '8px' }}>
                      <button
                        onClick={() => handleApproveReject(leave.id, 'approved')}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '6px',
                          padding: '10px 20px',
                          background: 'linear-gradient(135deg, #10B981, #059669)',
                          color: 'white',
                          border: 'none',
                          borderRadius: '8px',
                          cursor: 'pointer',
                          fontWeight: '600',
                          fontSize: '13px'
                        }}
                        data-testid={`approve-leave-${leave.id}`}
                      >
                        <Check size={16} />
                        Approve
                      </button>
                      <button
                        onClick={() => {
                          const reason = window.prompt('Please provide a reason for rejection (optional):');
                          handleApproveReject(leave.id, 'rejected', reason || '');
                        }}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '6px',
                          padding: '10px 20px',
                          background: 'rgba(239, 68, 68, 0.1)',
                          color: '#EF4444',
                          border: 'none',
                          borderRadius: '8px',
                          cursor: 'pointer',
                          fontWeight: '600',
                          fontSize: '13px'
                        }}
                        data-testid={`reject-leave-${leave.id}`}
                      >
                        <X size={16} />
                        Reject
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Apply Leave Modal */}
      {showApplyModal && (
        <div className="modal-overlay" onClick={() => setShowApplyModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '500px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
              <h2 style={{ margin: 0, fontSize: '20px', fontWeight: '600', color: 'var(--text-primary)', display: 'flex', alignItems: 'center', gap: '10px' }}>
                <Coffee size={24} color="#F59E0B" />
                Apply for Leave
              </h2>
              <button
                onClick={() => setShowApplyModal(false)}
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-tertiary)' }}
              >
                <X size={24} />
              </button>
            </div>

            <form onSubmit={handleApplyLeave}>
              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500', color: 'var(--text-primary)', fontSize: '14px' }}>
                  Leave Type *
                </label>
                <select
                  value={formData.leave_type}
                  onChange={(e) => setFormData({ ...formData, leave_type: e.target.value })}
                  style={{
                    width: '100%',
                    padding: '12px',
                    border: '1px solid var(--border-color)',
                    borderRadius: '8px',
                    background: 'var(--bg-primary)',
                    color: 'var(--text-primary)',
                    fontSize: '14px'
                  }}
                  data-testid="leave-type-select"
                >
                  {LEAVE_TYPES.map(lt => (
                    <option key={lt.value} value={lt.value}>{lt.label}</option>
                  ))}
                </select>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '20px' }}>
                <div>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500', color: 'var(--text-primary)', fontSize: '14px' }}>
                    Start Date *
                  </label>
                  <input
                    type="date"
                    value={formData.start_date}
                    onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                    min={new Date().toISOString().split('T')[0]}
                    required
                    style={{
                      width: '100%',
                      padding: '12px',
                      border: '1px solid var(--border-color)',
                      borderRadius: '8px',
                      background: 'var(--bg-primary)',
                      color: 'var(--text-primary)',
                      fontSize: '14px'
                    }}
                    data-testid="start-date-input"
                  />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500', color: 'var(--text-primary)', fontSize: '14px' }}>
                    End Date *
                  </label>
                  <input
                    type="date"
                    value={formData.end_date}
                    onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
                    min={formData.start_date || new Date().toISOString().split('T')[0]}
                    required
                    style={{
                      width: '100%',
                      padding: '12px',
                      border: '1px solid var(--border-color)',
                      borderRadius: '8px',
                      background: 'var(--bg-primary)',
                      color: 'var(--text-primary)',
                      fontSize: '14px'
                    }}
                    data-testid="end-date-input"
                  />
                </div>
              </div>

              {formData.start_date && formData.end_date && (
                <div style={{
                  padding: '12px 16px',
                  background: '#7077FE10',
                  borderRadius: '8px',
                  marginBottom: '20px',
                  color: '#7077FE',
                  fontSize: '14px',
                  fontWeight: '500'
                }}>
                  Total: {calculateDays(formData.start_date, formData.end_date)} day{calculateDays(formData.start_date, formData.end_date) > 1 ? 's' : ''}
                </div>
              )}

              <div style={{ marginBottom: '24px' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500', color: 'var(--text-primary)', fontSize: '14px' }}>
                  Reason *
                </label>
                <textarea
                  value={formData.reason}
                  onChange={(e) => setFormData({ ...formData, reason: e.target.value })}
                  placeholder="Please provide a reason for your leave request..."
                  required
                  rows={4}
                  style={{
                    width: '100%',
                    padding: '12px',
                    border: '1px solid var(--border-color)',
                    borderRadius: '8px',
                    background: 'var(--bg-primary)',
                    color: 'var(--text-primary)',
                    fontSize: '14px',
                    resize: 'vertical'
                  }}
                  data-testid="reason-textarea"
                />
              </div>

              <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
                <button
                  type="button"
                  onClick={() => setShowApplyModal(false)}
                  style={{
                    padding: '12px 24px',
                    border: '1px solid var(--border-color)',
                    borderRadius: '8px',
                    background: 'transparent',
                    color: 'var(--text-primary)',
                    cursor: 'pointer',
                    fontWeight: '500'
                  }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="gradient-btn"
                  style={{ padding: '12px 24px', display: 'flex', alignItems: 'center', gap: '8px' }}
                  data-testid="submit-leave-btn"
                >
                  <Send size={16} />
                  Submit Request
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default LeaveManagement;
